import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Briefcase,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  Send,
  Video,
  X,
} from "lucide-react";
import { createPost, getAllPosts } from "reducer/postsSlice";
import { API } from "service/Interceptor";
import { compressImage, serverFiles } from "utils/utils";
import toast from "components/Toast/toast";
import BriefModal from "components/app/BriefModal";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const TAGS = [
  { label: "Network", value: "NETWORK" },
  { label: "Advice", value: "ADVICE" },
  { label: "Thoughts", value: "THOUGHTS" },
];

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const FeedComposer = ({ onPosted }) => {
  const dispatch = useDispatch();
  const { userProfile } = useSelector((state) => state.userProfile);

  const [content, setContent] = useState("");
  const [tag, setTag] = useState("NETWORK");
  const [busy, setBusy] = useState(false);
  const [briefOpen, setBriefOpen] = useState(false);
  const [media, setMedia] = useState(null);

  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const fileRef = useRef(null);

  const isBusiness = userProfile?.userType === "BUSINESS";
  const name = isBusiness
    ? userProfile?.companyResponseDto?.name
    : `${userProfile?.firstName || ""} ${userProfile?.lastName || ""}`.trim();
  const avatarPath =
    userProfile?.companyResponseDto?.logo || userProfile?.profileUrl;
  const avatarSrc = avatarPath ? serverFiles(avatarPath) : null;

  const pick = (kind) => () => {
    const map = { image: photoRef, video: videoRef, file: fileRef };
    map[kind]?.current?.click();
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    if (isVideo && file.size > 20 * 1024 * 1024) {
      toast.error("Video exceeds 20MB limit");
      return;
    }

    let fileToUpload = file;
    if (isImage) {
      try {
        fileToUpload = await compressImage(file);
      } catch (_) {
        /* use original */
      }
    }

    const previewUrl = URL.createObjectURL(fileToUpload);
    const tempId = Math.random().toString(36).slice(2);
    setMedia({
      id: tempId,
      previewUrl,
      uploading: true,
      type: isImage ? "image" : isVideo ? "video" : "file",
      name: file.name,
      path: null,
    });

    const formData = new FormData();
    formData.append("fileName", fileToUpload);

    try {
      const res = await API.post("api/media", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded = res?.data?.[0];
      if (!uploaded?.id) throw new Error("Upload failed");
      setMedia({
        id: uploaded.id,
        path: uploaded.path,
        previewUrl: null,
        uploading: false,
        type: isImage ? "image" : isVideo ? "video" : "file",
        name: file.name,
      });
    } catch (err) {
      console.error(err);
      toast.error("Media upload failed");
      setMedia(null);
    }
  };

  const clearMedia = () => setMedia(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() || busy) return;
    if (content.trim().length < 10) {
      toast.error("Minimum 10 characters required");
      return;
    }
    if (media?.uploading) {
      toast.error("Wait for media upload to finish");
      return;
    }

    setBusy(true);
    try {
      const payload = {
        tags: [tag],
        content: content.trim(),
        mediaIds: media?.id && !media.uploading ? [media.id] : [],
      };
      const res = await dispatch(createPost(payload));
      if (res?.payload) {
        toast.success("Posted successfully");
        setContent("");
        setMedia(null);
        setTag("NETWORK");
        dispatch(
          getAllPosts({ pageNo: 1, perPage: 5, sortBy: "latest" })
        );
        onPosted?.();
      } else {
        toast.error("Could not create post");
      }
    } catch (err) {
      toast.error(err?.message || "Could not create post");
    } finally {
      setBusy(false);
    }
  };

  const mediaPreviewSrc = media?.previewUrl
    ? media.previewUrl
    : media?.path
      ? serverFiles(media.path)
      : null;

  return (
    <>
      <form
        onSubmit={submit}
        className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-5"
      >
        <div className="flex items-start gap-2.5 sm:gap-3">
          <Link to="/user-profile" className="shrink-0">
            {avatarSrc ? (
              <img
                src={avatarSrc}
                alt={name}
                className="h-9 w-9 sm:h-11 sm:w-11 rounded-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = NO_IMAGE;
                }}
              />
            ) : (
              <div className="avatar-fallback h-9 w-9 sm:h-11 sm:w-11 rounded-full flex items-center justify-center font-semibold text-sm">
                {initials(name)}
              </div>
            )}
          </Link>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={media ? 2 : 2}
            placeholder="Share an update, a shoot or a brief…"
            className="flex-1 min-w-0 resize-none bg-transparent border-0 shadow-none ring-0 outline-none text-sm text-zinc-100 placeholder:text-zinc-500 pt-2 focus:ring-0 focus:outline-none"
          />
        </div>

        {media && (
          <div className="mt-3 sm:ml-[56px] relative inline-block max-w-full">
            {media.uploading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-zinc-950/60">
                <Loader2 size={18} className="animate-spin text-[#8b83ff]" />
              </div>
            )}
            {media.type === "video" ? (
              <video
                src={mediaPreviewSrc}
                controls
                className="max-h-52 w-full max-w-full rounded-xl border border-zinc-800"
              />
            ) : media.type === "file" ? (
              <div className="inline-flex items-center gap-3 rounded-xl border border-zinc-800 bg-zinc-900/60 px-4 py-3 max-w-full">
                <FileText size={18} className="text-[#8b83ff] shrink-0" />
                <span className="text-sm text-zinc-100 max-w-[min(280px,60vw)] truncate">
                  {media.name}
                </span>
              </div>
            ) : (
              <img
                src={mediaPreviewSrc}
                alt=""
                className="max-h-52 w-full max-w-full rounded-xl border border-zinc-800 object-cover"
              />
            )}
            <button
              type="button"
              onClick={clearMedia}
              className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-zinc-950 border border-zinc-700 flex items-center justify-center text-zinc-300 hover:text-white"
            >
              <X size={13} />
            </button>
          </div>
        )}

        <div className="mt-4 sm:pl-[56px] flex flex-wrap items-center gap-2">
          {TAGS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTag(t.value)}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                tag === t.value
                  ? "border-[#6C63F5] bg-[#6C63F5]/15 text-zinc-50"
                  : "border-zinc-800 bg-zinc-900/50 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setBriefOpen(true)}
            className="rounded-full border border-[#6C63F5]/40 bg-[#6C63F5]/10 text-[#8b83ff] px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-[#6C63F5]/20 transition-colors"
          >
            <Briefcase size={13} />{" "}
            <span className="hidden xs:inline sm:inline">Post a brief</span>
            <span className="sm:hidden">Brief</span>
          </button>
        </div>

        <div className="mt-3 flex items-center gap-0.5 sm:gap-2 sm:pl-[56px]">
          <input
            ref={photoRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onFilePicked}
          />
          <input
            ref={videoRef}
            type="file"
            accept="video/*"
            hidden
            onChange={onFilePicked}
          />
          <input ref={fileRef} type="file" hidden onChange={onFilePicked} />
          <button
            type="button"
            onClick={pick("image")}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
          >
            <ImageIcon size={15} className="text-[#8b83ff]" />
            <span className="hidden sm:inline">Photo</span>
          </button>
          <button
            type="button"
            onClick={pick("video")}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
          >
            <Video size={15} className="text-[#8b83ff]" />
            <span className="hidden sm:inline">Video</span>
          </button>
          <button
            type="button"
            onClick={pick("file")}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 sm:px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
          >
            <Paperclip size={15} className="text-[#8b83ff]" />
            <span className="hidden sm:inline">File</span>
          </button>
          <button
            type="submit"
            disabled={busy || !content.trim() || media?.uploading}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#6C63F5] text-white px-4 py-1.5 text-xs font-semibold hover:bg-[#5751E5] transition-colors disabled:opacity-50 shrink-0 w-auto"
          >
            {busy ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Send size={14} />
            )}
            Post
          </button>
        </div>
      </form>

      <BriefModal
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        onPosted={() => onPosted?.()}
      />
    </>
  );
};

export default FeedComposer;
